import type { GetStaticPropsContext } from 'next';
import Image from 'next/image';
import { pick } from 'lodash-es';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { FAQSection } from '~/components/faq-section';
import { SanityImage } from '~/components/sanity-image';
import { AIResourcesSection } from '~/features/ai-campaign/components/ai-resources-section';
import { BlurbSection } from '~/features/ai-campaign/components/blurb-section';
import { HeroSection } from '~/features/ai-campaign/components/hero-section';
import { PartnershipsSection } from '~/features/ai-campaign/components/partnerships-section';
import { PopularCoursesSection } from '~/features/ai-campaign/components/popular-courses-section';
import { SkillLevelCollection } from '~/features/ai-campaign/components/skill-level-collection-section';
import { TestimonialsSection } from '~/features/ai-campaign/components/testimonials-section';
import testimonials from '~/features/ai-campaign/testimonials.json';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { getAllCaseStudies } from '~/features/case-study/controllers/get-all-case-studies';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { getCompanies } from '~/features/companies/controllers/get-companies';
import { CompanyContent } from '~/features/companies/models/company';
import { getAllEnterpriseResources } from '~/features/enterprise-resource/controllers/get-all-enterprise-resources';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { EnterpriseCenterResource } from '~/features/page-template/templates/enterprise-resource-center/types';
import { convertResourcesToStandardFormat } from '~/features/page-template/templates/enterprise-resource-center/utils';
import { IndividualPlansSectionV2 } from '~/features/plans/components/individual-plans-section-v2';
import { ForBusinessCard } from '~/features/plans/components/individual-plans-section-v2/for-business-card';
import { getRelatedPrograms } from '~/features/program/controllers/get-related-programs';
import { getSubscriptionSeoPriceData } from '~/features/seo/controllers/get-subscription-seo-price-data';
import {
  createSubscriptionStructuredData,
  serializeSubscriptionStructuredData,
} from '~/features/seo/utils/create-subscription-structured-data';

const PAGE_URL = 'https://www.udacity.com/ai';

export default function AICampaignLandingPage({
  page,
  aiResources,
  partners,
  instructors,
  relatedPrograms,
  structuredData,
}: {
  page: Page;
  aiResources: EnterpriseCenterResource[];
  partners: Array<Pick<CompanyContent, '_id' | 'name' | 'hifiLogo'>>;
  instructors: Array<{
    name: string;
    title: string;
    imagePath: string;
    company: Pick<CompanyContent, '_id' | 'name' | 'hifiLogo'>;
  }>;
  relatedPrograms: CatalogItem[];
  structuredData: string;
}) {
  return (
    <>
      <PageHead
        page={page}
        pageCategory={PageCategory.Skills}
        additionalPageAnalytics={{
          label: 'Artificial Intelligence',
        }}
      />
      <PageLayout withoutVerticalPadding>
        <HeroSection
          blurb='Master AI with real-world projects and courses co-created with AI experts'
          title={`Forge your\nfuture in AI`}
          buttonSlot={
            <ButtonLink
              buttonProps={{
                rightIcon: <ArrowRightIcon h='24px' w='24px' />,
                size: 'icon-right',
                width: { base: '100%', md: 'fit-content' },
                onClick: () =>
                  trackEvent({
                    type: 'click',
                    name: 'skills_page_cta_clicked',
                    label: 'enroll_now',
                  }),
              }}
              linkProps={{
                href: '#plans',
              }}
            >
              Enroll Now
            </ButtonLink>
          }
          features={[
            {
              glyphName: 'network-six-two',
              description: 'The original home for learning AI online',
            },
            {
              glyphName: 'computer-mouse-two',
              description: 'Hands-on projects with feedback from mentors',
            },
            {
              glyphName: 'lightbulb-head-three',
              description: 'AI training delivered by subject matter experts',
            },
            {
              glyphName: 'alert-three-one',
              description: 'Learn with the latest AI tools and tech',
            },
          ]}
        />
        <BlurbSection
          blurb={`Udacity sprang from a simple idea: how can we bring the best AI education from Silicon Valley to the rest of the world? That's what Udacity co-founder and Stanford instructor Sebastian Thrun had in mind when he first made his "Introduction to Artificial Intelligence" course available online to anyone in the world. Over a decade later, Udacity remains the best place for professionals to build AI skills, whether you're already based in the top tech companies of Silicon Valley, or just starting to plan your future in AI.`}
          title="Whether you're just beginning your journey, or you're looking for the latest in advanced AI skills, you've come to the right place."
        />
        <PopularCoursesSection
          blurb='Discover the courses that our students love the most for building real-world AI skills and knowledge.'
          programKeys={['nd900', 'nd189', 'nd608', 'nd881', 'nd101']}
          title='Our most popular AI courses'
        />
        <SkillLevelCollection
          analyticsLabel='beginner_courses'
          badgeText='Beginner & Intermediate courses'
          blurb='Your journey into the world of Artificial Intelligence begins here:'
          programKeys={['nd089', 'ud120', 'cd13232', 'nd054']}
          skillLevel='intermediate'
          title='Getting started with AI'
          imageSlot={
            <Box
              borderRadius='32px 8px'
              overflow='hidden'
              position='relative'
              width='full'
              height={{
                md: '496px',
                xl: '706px',
              }}
            >
              <Image
                alt=''
                sizes='869px'
                src='/images/ai/person-using-laptop-with-headphones.png'
                style={{
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  display: 'block',
                }}
                fill
              />
            </Box>
          }
        />
        <SkillLevelCollection
          analyticsLabel='advanced_courses'
          badgeText='Advanced courses'
          blurb='Elevate your expertise and master cutting-edge AI technologies:'
          imagePosition='left'
          programKeys={['nd900', 'nd881', 'nd101', 'nd770']}
          skillLevel='advanced'
          theme='sea-foam'
          title='Advance your AI skill set'
          imageSlot={
            <Box
              borderRadius='32px 8px'
              overflow='hidden'
              position='relative'
              width='full'
              height={{
                md: '496px',
                xl: '706px',
              }}
            >
              <Image
                alt=''
                sizes='869px'
                src='/images/ai/vr-developer-working-on-game-code.png'
                style={{
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  display: 'block',
                }}
                fill
              />
            </Box>
          }
        />
        <AIResourcesSection resources={aiResources} title='The latest AI resources' />
        <PartnershipsSection
          partners={partners}
          testimonialsSlot={<TestimonialsSection testimonials={testimonials} title='AI Success Stories' />}
          title='Programs Co-created With Artificial Intelligence Leaders'
          instructorsSlot={
            <Flex
              gap={6}
              flexDir={{
                base: 'column',
                md: 'row',
              }}
            >
              {instructors.map((instructor) => (
                <Card
                  key={instructor.name}
                  alignItems='center'
                  as='article'
                  borderColor='gray.200'
                  color='black'
                  display='flex'
                  flex={{ md: '1 1 357px' }}
                  gap={6}
                  p={4}
                >
                  <Image
                    alt=''
                    height={104}
                    sizes='208px'
                    src={instructor.imagePath}
                    width={104}
                    style={{
                      pointerEvents: 'none',
                      objectFit: 'contain',
                    }}
                  />
                  <Flex flexDir='column' gap={1}>
                    <Heading as='p' size='marketing-lg'>
                      {instructor.name}
                    </Heading>
                    <Text fontSize='xs'>{instructor.title}</Text>
                    {instructor?.company?.hifiLogo && (
                      <SanityImage
                        alt=''
                        image={instructor.company.hifiLogo}
                        style={{
                          pointerEvents: 'none',
                          objectFit: 'scale-down',
                          objectPosition: 'left',
                        }}
                      />
                    )}
                  </Flex>
                </Card>
              ))}
            </Flex>
          }
        />
        <Box id='plans' scrollMarginTop={headerHeight}>
          <IndividualPlansSectionV2
            footerSlot={<ForBusinessCard />}
            relatedPrograms={relatedPrograms}
            title='Choose the right learning path for your goals'
          />
        </Box>
        <FAQSection
          faq={[
            {
              _id: '1',
              question: 'How can I learn AI?',
              answer: `There are multiple aspects of AI that you may be interested in. If you're looking to become a technical practitioner who builds models and works directly highly technical environments, consider taking Udacity's practitioner-level and skills based programs like [Generative AI](/course/generative-ai--nd608), [AWS ML Engineer](/course/aws-machine-learning-engineer-nanodegree--nd189) and [Machine Learning DevOps](/course/machine-learning-dev-ops-engineer-nanodegree--nd0821), [Deep Reinforcement Learning](/course/deep-reinforcement-learning-nanodegree--nd893), AI Specialties like [AI-Powered Software Engineer](/course/ai-powered-software-engineer--nd770) and [Natural Language Processing](/course/natural-language-processing-nanodegree--nd892), or Industry-based content like [AI Trading Strategies](/course/ai-trading-strategies--nd881) or [AI for Healthcare](/course/ai-for-healthcare-nanodegree--nd320). If you’re interested in integrating AI into your current work processes, you can consider engaging with content that builds your strategic AI competency like [Generative AI Fundamentals](/course/generative-ai-fundamentals--cd13303), [Generative AI for Business Leaders](/course/generative-AI-for-business-leaders--cd13230), or [AI for Business Leaders](/course/ai-for-business-leaders--nd054). Those are who just starting out, with no technical background can start by learning the language and underlying foundations of AI without any prior coding knowledge with courses like [Generative AI Fluency](/course/generative-ai-fluency--cd13224), [Discovering Artificial Intelligence and Machine Learning](/course/discovering-ai-ml--cd2019), or [Discovering Ethical AI](/course/discovering-ethical-AI--cd13462). You can also start to experiment and build with Udacity with  [How to Use ChatGPT Fluency](/course/how-to-use-chatgpt--cd13054), [Digital Arts and AI Tools Fluency](/course/digital-arts-and-ai-tools-fluency--cd12879), [Introducing Generative AI with AWS](/course/generative-AI-with-AWS--cd13232), or [Introducing Generative AI with Google Cloud](/course/introduction-generative-ai-google-cloud--cd12957).`,
            },
            {
              _id: '2',
              question: 'What jobs are there in AI?',
              answer: `AI jobs have come a long way in just a few years. Not too long ago, working in AI meant being deeply specialized in tech, often coding all day or researching complex algorithms. But now, AI is everywhere, and it's touching almost every industry. From marketing to healthcare, and finance to entertainment, AI is no longer just for tech gurus. The best part? You don't even need to be a hardcore coder to be part of this world. AI is becoming more accessible, and there's a growing need for people with different skills, not just technical ones. Here are five AI job roles, including two that dont require coding (yes, really!):

1. **AI/ML Engineer (for example, [Azure](/course/machine-learning-engineer-for-microsoft-azure-nanodegree--nd00333) or [AWS](/course/aws-machine-learning-engineer-nanodegree--nd189))**  
   This is one of the more traditional AI roles where you're in the trenches, building and deploying machine learning models. AI/ML Engineers work with large datasets, training algorithms to make predictions, automate tasks, and recognize patterns. If you love coding (Python is your best friend here) and solving tricky problems, this could be the role for you. It's hands-on, technical, and incredibly rewarding for those who enjoy the challenge of building things from the ground up.  
2. [**AI Product Manager**](/course/ai-product-manager-nanodegree--nd088)  
   If you’re more into strategy and less into code, AI Product Manager might be your jam. You'll be guiding AI projects from start to finish, making sure the final product actually makes sense for users and businesses. *You don't need to know how to code for this role*, but understanding AI's capabilities and staying on top of industry trends is key. You'll be working with engineers, data scientists, and designers, helping to turn ideas into reality. It's all about the big picture and making sure that AI is being used in ways that add real value.  
3. **AI Consultant**  
   Not a coder? No problem! As an AI Consultant, your role is to help businesses figure out how to use AI to improve what they're doing. You'll be looking at workflows and processes, spotting where AI can make things better or faster, and then guiding companies on how to integrate these tools. It's a great role if you enjoy problem-solving and working closely with people. You'll need to understand AI, of course, but this role is more about strategy and communication than sitting behind a screen coding all day.  
4. **AI Content Creator/Creative Specialist**  
   Love creativity? This role blends art and technology. AI Content Creators use AI tools to generate creative work—think AI-generated art, music, video, and even writing. Whether you're working with AI to help create digital paintings, generate music, or craft unique videos, this is where AI meets creativity. You don't need to be a coder here, but you do need to understand how to use AI tools to elevate your creative work. This role is perfect if you're someone who loves innovation and wants to explore the artistic potential of AI.  
5. [**Data Scientist**](/course/data-scientist-nanodegree--nd025)  
   Data Scientists are the detectives of the AI world. You dig into data, analyze it, and find patterns that help drive decisions. Yes, this role involves some coding (Python is often used), but it's also about storytelling. You take the insights you uncover and communicate them to people who may not be data experts. It's all about turning raw data into something meaningful that businesses can act on. If you're curious and love finding the “why” behind the numbers, this role could be a perfect fit.

These roles show that there's no one-size-fits-all path into AI. Whether you're technically inclined, a creative thinker, or a strategic planner, there's a place for you in this ever-evolving field. And with AI becoming more integrated into everyday jobs, the possibilities are only expanding!`,
            },
            {
              _id: '3',
              question: 'What skills do you need to work in AI?',
              answer: `The skills you need to work in AI depend a lot on what kind of role you're aiming for, but one thing is for sure: AI is more than just coding! Whether you're diving deep into technical roles or exploring creative and strategic opportunities, some key skills that can help you thrive in the AI world:

* **Curiosity and Continuous Learning**  
  The world of AI moves fast, and staying relevant means you'll need a strong sense of curiosity and a commitment to lifelong learning. Whether you're a seasoned AI professional or just starting out, there will always be new tools, algorithms, and techniques to explore. Embrace this! A willingness to dive into new technologies, keep up with the latest research, and continuously refine your skills is essential. This mindset will keep you growing and thriving in an ever-changing field.  
    
* **Ethical Awareness**  
  As AI becomes more integrated into our lives, there's growing attention on the ethical implications of AI. If you're working in AI, you need to be aware of these issues, whether it's about data privacy, bias in algorithms, or the impact of AI on jobs.   
* **Analytical Thinking**  
  No matter your role, being able to think critically and solve problems is a must. AI is all about figuring out how to make systems smarter and more efficient, and that takes creativity and a strong analytical mindset. The ability to approach problems logically and strategically is key.  
    
* **Technical Skills**  
  If you're going for a technical role, like an AI/ML Engineer or Data Scientist, you'll need a strong foundation in programming—Python is the most common language in AI. You'll also need to know your way around machine learning frameworks like TensorFlow, PyTorch, or Keras. Beyond coding, understanding algorithms, data structures, and statistical modeling is crucial.`,
            },
            {
              _id: '4',
              question: 'What programming languages are used in AI?',
              answer: `  
Hands down, [Python](/course/ai-programming-python-nanodegree--nd089) is the rockstar of AI programming. Think of it as the friendly neighborhood superhero—easy to learn, incredibly versatile, and backed by a powerful crew of sidekicks (aka libraries) like [TensorFlow](/course/intro-to-machine-learning-with-tensorflow-nanodegree--nd230) and [PyTorch](/course/intro-to-machine-learning-nanodegree--nd229), which make even the most complex AI tasks feel manageable. 

But Python's popularity isn't just because it's beginner-friendly. It's also highly compatible with production environments, allowing AI features—like chatbots, AI assistants, and intelligent agents—to be smoothly integrated into existing systems. That's why Python isn't just a favorite for prototyping and development; it's also the top choice for deploying AI solutions in real-world applications.

While Python takes the spotlight, it's important to remember that languages like [Java](/course/java-programming-nanodegree--nd079) and [C++](/course/c-plus-plus-nanodegree--nd213) also play key roles in AI, especially in large-scale enterprise applications and performance-critical systems. Python's flexibility, however, lets it work well alongside these languages, making it the backbone of many AI-powered production environments.`,
            },
          ]}
          titleSlot={
            <Heading
              as='h2'
              size='marketing-4xl'
              textAlign='center'
              marginBottom={{
                base: '24px',
                md: '40px',
              }}
            >
              General FAQs
            </Heading>
          }
        />
      </PageLayout>
      <script dangerouslySetInnerHTML={{ __html: structuredData }} type='application/ld+json' />
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const partners = [
    'Kaggle',
    'AWS',
    'Nvidia - Deep Learning Institute',
    'BMW',
    'IBM Watson',
    'Facebook AI',
    'Affectiva',
    'Amazon Alexa',
    'Intel',
    'Worldquant',
  ];

  const logoProps = ['_id', 'name', 'hifiLogo'] as const;

  const [page, allCaseStudies, allEnterpriseResources, partnerCompanies, instructorCompanies, priceData] =
    await Promise.all([
      getPage('/ai'),
      getAllCaseStudies(),
      getAllEnterpriseResources(),
      getCompanies(partners).then((companies) => companies.map((company) => pick(company, logoProps))),
      getCompanies(['Netflix', 'Nvidia', 'Intel']).then((companies) =>
        companies.map((company) => pick(company, logoProps))
      ),
      getSubscriptionSeoPriceData(),
    ]);

  const combinedResources = [...allEnterpriseResources, ...allCaseStudies]
    .sort((a, b) => b._updatedAt.localeCompare(a._updatedAt))
    .filter((resource) => !resource.excludeFromResourceCenter);

  const enterpriseCenterResources = convertResourcesToStandardFormat(combinedResources);

  const structuredData = serializeSubscriptionStructuredData(
    createSubscriptionStructuredData({
      url: PAGE_URL,
      name: 'Udacity Subscription',
      description: page.seo.description,
      breadcrumbName: 'AI',
      priceData,
    })
  );

  return {
    props: {
      page,
      aiResources: enterpriseCenterResources.filter((resource) =>
        resource.knowledgeAreas?.includes('artificialIntelligence')
      ),
      partners: [...partnerCompanies].sort((a, b) => partners.indexOf(a.name) - partners.indexOf(b.name)), // Note: Sort by order of partners array
      instructors: [
        {
          name: 'Judit Lantos',
          title: 'Senior Data Engineer at Netflix',
          imagePath: '/images/ai/lantos.png',
          company: instructorCompanies.find((company) => company.name === 'Netflix') || null,
        },
        {
          name: 'Erick Galinkin',
          title: 'AI Security Researcher, NVIDIA',
          imagePath: '/images/ai/galinkin.png',
          company: instructorCompanies.find((company) => company.name === 'Nvidia') || null,
        },
        {
          name: 'Ria Cheruvu',
          title: 'AI Software Architect, Intel',
          imagePath: '/images/ai/cheruvu.png',
          company: instructorCompanies.find((company) => company.name === 'Intel') || null,
        },
      ],
      relatedPrograms: getRelatedPrograms(),
      structuredData,
    },
  };
}
